import React, { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import CustomSelectDropdown from "../../../../../Components/custom_components/CustomSelectDropdown/CustomSelectDropdown";
import CustomDatePicker from "../../../../../Components/custom_components/CustomDatePicker/CustomDatePicker";
import ActionButton from "../../../../../Components/custom_components/CustomButtons/ActionButton/ActionButton";
import { get_report_status } from "../../../../../config/GeneralApis/GeneralApis";
import CustomTextArea from "../../../../../Components/custom_components/CustomTextArea/CustomTextArea";
import { convertToISO } from "../../../../../utils/formatDate";
import { useNavigate, useParams } from "react-router-dom";
import { getReportsById } from "../../../../../config/ReportApis/scheduleReportApis";
import { ErrorToast } from "../../../../../utils/toast";
import ReportDetailsView from "../ReportDetailsView/ReportDetailsView";
import { get_workforce_with_pagination } from "../../../../../config/Resource/workforceApis";



const ReportStatusChangeForm = ({ scheduleId, reportId, handleSave, isButtonDisabled, formType, defaultValues, confirm_function }) => {
    const navigate = useNavigate();
    // const { scheduleId, reportId } = useParams();

    const [engineerOptions, setEngineerOptions] = useState([]);
    const [allStatusOptions, setAllStatusOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [reportData, setReportData] = useState({});

    const formik = useFormik({
        initialValues: {
            status: "",
            start_date: null,
            end_date: null,
            assigned_id: [],
            shortDescription: "",
        },
        validationSchema: Yup.object().shape({
            status: Yup.string().required("Status is required"),
            start_date: Yup.string().required("Start Date is required"),
            end_date: Yup.string()
                .required("End Date is required")
                .test(
                    "end-date-after-start-date",
                    "End date cannot be before start date",
                    function (value) {
                        const startDate = new Date(convertToISO(this.parent.start_date));
                        const endDate = new Date(convertToISO(value));
                        return endDate >= startDate;
                    }
                ),
            assigned_id: Yup.array().min(1, "Select at least one engineer").required(),
        }),
        onSubmit: (values) => {

            const startDateOnly = values.start_date ? convertToISO(values.start_date) : ''
            const endDateOnly = values.end_date ? convertToISO(values.end_date) : ''
            const deStructureValue = {
                ...values,
                start_date: startDateOnly,
                end_date: endDateOnly,
                status: values.status,
                ScheduleID: scheduleId,
                scheduleReport: [reportId]
            };
            handleSave(deStructureValue)

        },
    });

    useEffect(() => {
        fetchWorkforce();
        fetchReportStatus();
    }, []);

    useEffect(() => {
        if (reportId) {
            fetchReportDetails(reportId);
        }
    }, [reportId]);

    useEffect(() => {
        if (!reportData?.report_status || allStatusOptions.length === 0) return;

        const currentStatus = parseInt(reportData.report_status);

        if (formType === "reassign") {
            // Pre-fill all values for reassign
            formik.setValues({
                status: currentStatus,
                start_date: reportData.start_date || null,
                end_date: reportData.end_date || null,
                assigned_id: reportData?.assigned_ids?.map(item => item.id) || [],
                shortDescription: reportData?.description || "",
            });
            setStatusOptions(allStatusOptions);
        } else {
            // For normal flow, show only next status options
            const nextStatuses = allStatusOptions.filter(opt => opt.value > currentStatus);
            if (nextStatuses.length > 0) {
                setStatusOptions(nextStatuses);
                formik.setFieldValue("status", nextStatuses[0].value); // default to first next
            } else {
                setStatusOptions([]);
                formik.setFieldValue("status", "");
            }
        }
    }, [reportData, allStatusOptions, formType]);

    // FETCH fetchWorkforce
    const fetchWorkforce = async () => {
        try {
            const params = { //FOR GET ENGINEERS ONLY LIST
                RoleID: 5,
                status: 1
            }
            const response = await get_workforce_with_pagination(params);

            if (response?.status === "success") {
                // Transform data into the desired format with label and value
                const transformedData = response.data.data?.map((item) => ({
                    label: item?.UD_name,
                    value: item?.UD_ID
                }));

                // Set the transformed data to the state
                setEngineerOptions(transformedData || []);
            } else {
                setEngineerOptions([]);
            }
        } catch (error) {
            console.error('Error fetching :', error);
            setEngineerOptions([]);
        }
    };

    const fetchReportStatus = async () => {
        try {
            const response = await get_report_status();
            if (response?.status === "success") {
                const options = response.data.map(item => ({
                    label: item?.process,
                    value: item?.id,
                }));
                setAllStatusOptions(options);
            }
        } catch (error) {
            console.error("Status fetch error", error);
        }
    };

    const fetchReportDetails = async (id) => {
        try {
            const response = await getReportsById(id);
            if (response?.status === "success") {
                setReportData(response?.data || {});
            } else {
                ErrorToast(response?.message);
            }
        } catch (error) {
            ErrorToast("Something went wrong!");
        }
    };

    const isSubmittProcess = statusOptions?.find(o => o.value === formik.values.status)?.value == 5
    return (

        <>
            <ReportDetailsView reportData={reportData} />

            <div className="page-container mb-5">
                {/* 4 MEANS THE PROCESS IS SUBMIT  */}

                <Grid container spacing={3}>
                    {/* Status */}
                    <Grid item xs={12} md={6}>
                        <span className="label">
                            Next Process<span className="label_required_remark">*</span>
                        </span>
                        <CustomSelectDropdown
                            name="status"
                            placeholder="Select Status"
                            options={statusOptions}
                            value={statusOptions?.find(o => o.value === formik.values.status)}
                            onChange={option => formik.setFieldValue("status", option?.value || "")}
                            onBlur={formik.handleBlur}
                            error={formik.touched.status && Boolean(formik.errors.status)}
                            helperText={formik.touched.status && formik.errors.status}
                            isDisabled={statusOptions.length === 0 || formType === `reassign`}
                        />
                    </Grid>

                    {/* Show these only when not submitted */}
                    {!isSubmittProcess && (
                        <>
                            {/* Start Date */}
                            <Grid item xs={12} md={6}>
                                <span className="label">
                                    Start Date<span className="label_required_remark">*</span>
                                </span>
                                <CustomDatePicker
                                    name="start_date"
                                    label="Start Date"
                                    value={dayjs(formik.values.start_date, "DD-MM-YYYY")}
                                    onChange={date => formik.setFieldValue("start_date", date)}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.start_date && Boolean(formik.errors.start_date)}
                                    helperText={formik.touched.start_date && formik.errors.start_date}
                                    minDate={dayjs()}
                                />
                            </Grid>

                            {/* End Date */}
                            <Grid item xs={12} md={6}>
                                <span className="label">
                                    End Date<span className="label_required_remark">*</span>
                                </span>
                                <CustomDatePicker
                                    name="end_date"
                                    label="End Date"
                                    value={dayjs(formik.values.end_date, "DD-MM-YYYY")}
                                    onChange={date => formik.setFieldValue("end_date", date)}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.end_date && Boolean(formik.errors.end_date)}
                                    helperText={formik.touched.end_date && formik.errors.end_date}
                                    minDate={dayjs(formik.values.start_date, "DD-MM-YYYY")}
                                />
                            </Grid>

                            {/* Assigned Engineers */}
                            <Grid item xs={12} md={6}>
                                <span className="label">
                                    Engineers<span className="label_required_remark">*</span>
                                </span>

                                {/* new code  */}
                                {/* <CustomSelectDropdown
                            name="assigned_id"
                            placeholder="Select Engineer(s)"
                            options={engineerOptions}
                            value={engineerOptions.filter(opt =>
                                formik.values.assigned_id.includes(opt.value)
                            )}
                            onChange={options => {
                                const selectedIds = options.map(opt => opt.value);
                                formik.setFieldValue("assigned_id", selectedIds);
                            }}
                            onBlur={formik.handleBlur}
                            error={formik.touched.assigned_id && Boolean(formik.errors.assigned_id)}
                            helperText={formik.touched.assigned_id && formik.errors.assigned_id}
                            isMulti={true}
                            fixedOptions={formType === `reassign` ? reportData?.assigned_ids?.map(item => item.id) || [] : []}
                        /> */}

                                <CustomSelectDropdown
                                    name="assigned_id"
                                    placeholder="Select Engineer(s)"
                                    options={engineerOptions}
                                    value={engineerOptions.filter(opt =>
                                        formik.values.assigned_id.includes(opt.value)
                                    )}
                                    onChange={options => {
                                        const selectedIds = options.map(opt => opt.value);
                                        formik.setFieldValue("assigned_id", selectedIds);
                                    }}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.assigned_id && Boolean(formik.errors.assigned_id)}
                                    helperText={formik.touched.assigned_id && formik.errors.assigned_id}
                                    isMulti={true}

                                />


                            </Grid>

                            {/* Description */}
                            <Grid item xs={12}>
                                <CustomTextArea
                                    name="shortDescription"
                                    placeholder="Enter Description"
                                    value={formik.values.shortDescription}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.shortDescription && Boolean(formik.errors.shortDescription)}
                                    helperText={formik.touched.shortDescription && formik.errors.shortDescription}
                                    rows={5}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>

                {/* Buttons */}
                <div className="form-bottom-action-buttons-container mt-4">
                    <ActionButton onClick={() => navigate(-1)} label="Back" btnType="outlined" />
                    {isSubmittProcess ? (<ActionButton
                        btnDisable={isButtonDisabled}
                        onClick={() => confirm_function()}
                        label="Sent To Supervisor"
                        btnType="primary"
                    />) : (<ActionButton
                        btnDisable={isButtonDisabled}
                        onClick={formik.handleSubmit}
                        label="Save"
                        btnType="primary"
                    />)}
                </div>
            </div>
        </>

    );
};

export default ReportStatusChangeForm;
